import api from '../../../lib/axios';
import type { BasePagingResponse, BaseResponse } from '../../../lib/api-contract';
import {
    MerchantCouponStatisticRawApiResponse,
    MerchantRewardHistoryApiRequest,
    MerchantRewardHistoryItemApi,
    MerchantRewardHistoryRawApiResponse,
    MerchantRewardHistoryStatisticApi,
    ResendRewardResponse,
    mapRawApiResponseToApiResponse
} from './MerchantRewardHistory.data';

/**
 * List API: POST /v1/merchant/local-merchant-reward-history
 * Request: { pagination: { index, size }, filter: { user_name, user_email, coupon_name, redemption_status } }
 * Response: { success, data: { data: Item[], pagination: { count, index, size, total } } }
 */
export const getMerchantRewardHistoryList = async (
    request: MerchantRewardHistoryApiRequest
): Promise<BaseResponse<BasePagingResponse<MerchantRewardHistoryItemApi>>> => {
    const body = {
        pagination: {
            index: request.pagination.index,
            size: request.pagination.size,
        },
        filter: {
            user_name: request.filter.user_name ?? '',
            user_email: request.filter.user_email ?? '',
            coupon_name: request.filter.reward_name ?? '',
            redemption_status: request.filter.redemption_status ?? '',
        },
    };

    const response = await api.post<BaseResponse<MerchantRewardHistoryRawApiResponse>>(
        '/v1/merchant/local-merchant-reward-history',
        body,
        { headers: { 'Content-Type': 'application/json' } }
    );

    const payload = response.data?.data;
    if (!payload) {
        return {
            success: response.data?.success ?? false,
            code: response.data?.code ?? 0,
            http_request_status: response.data?.http_request_status ?? 0,
            message: response.data?.message ?? '',
            user_message: response.data?.user_message ?? '',
            errors: response.data?.errors ?? [],
            data: {
                paging: { count: 0, index: 0, size: body.pagination.size, total: 0 },
                list: [],
            },
        };
    }

    const list = payload.data.map(mapRawApiResponseToApiResponse);
    const pag = payload.pagination;

    return {
        success: response.data.success,
        code: response.data.code,
        http_request_status: response.data.http_request_status,
        message: response.data.message,
        user_message: response.data.user_message,
        errors: response.data.errors,
        data: {
            paging: { count: pag.total, index: pag.index, size: pag.size, total: pag.total },
            list,
        },
    };
};

/**
 * Statistic API: GET /v1/merchant/local-merchant-coupon/statistic
 * Response: { success, data: { total_active_coupons, total_coupons, coupons_redeemed, usage_count, ... } }
 * Mapped to reward history: total_redeemed_rewards = coupons_redeemed, total_sent_rewards = usage_count
 */
export const getMerchantRewardHistoryStatistic = async (): Promise<BaseResponse<MerchantRewardHistoryStatisticApi>> => {
    const response = await api.get<BaseResponse<MerchantCouponStatisticRawApiResponse>>(
        '/v1/merchant/local-merchant-coupon/statistic'
    );
    const raw = response.data?.data;
    const data: MerchantRewardHistoryStatisticApi = raw
        ? { total_redeemed_rewards: raw.coupons_redeemed, total_sent_rewards: raw.usage_count }
        : { total_redeemed_rewards: 0, total_sent_rewards: 0 };
    return {
        success: response.data?.success ?? false,
        code: response.data?.code ?? 0,
        http_request_status: response.data?.http_request_status ?? 0,
        message: response.data?.message ?? '',
        user_message: response.data?.user_message ?? '',
        errors: response.data?.errors ?? [],
        data,
    };
};

export const resendMerchantReward = async (
    userCouponId: string
): Promise<BaseResponse<ResendRewardResponse>> => {
    const response = await api.post<BaseResponse<ResendRewardResponse>>(
        `/v1/portal/reward-history/resend/${userCouponId}`
    );
    return response.data;
};

